import React, { useContext, useState, useEffect, useRef } from "react"
import { Html } from "@react-three/drei"
import styled from "@emotion/styled"
import { AppContext } from '../../context'
import { Label } from "./gui";
import CoverPage from '../pages/cover/index';
import * as THREE from 'three'
import { useTransition, a } from 'react-spring'
import { themeColor } from '../layout/global-style';
import { Loader } from "../atoms/loader";
import { usePreload } from "./usePreload";

const Container = styled(Html)`
    display: flex;
    padding: 0;
    position: absolute; 
    justify-content: center;
    align-items: center; 
    top: 0px;
    left: 0px;
    width: 100vw;
    height: 100vh;    
    background-color:  ${props => props.color};
`

const LoadingLabel = styled(Label)`
  font-size: 1.4em;
  line-height: 2em;
  color: ${props => props.color}; 
`
export const Loading = ({label = 'loading', onStartLoading, onEndLoading}) => {
  const { ready } = useContext(AppContext);
  const foregroundColor = ready ? themeColor.white : themeColor.black;
  const backgroundColor = ready ? themeColor.black : themeColor.white;

  useEffect(() => {
    // console.log(" Loading start");
    onStartLoading && onStartLoading();
    return () => {
      onEndLoading && onEndLoading();
      // console.log("Loading end");
    };
  }, [])

  return (
    <Container color={backgroundColor} center>
      <LoadingBar color={foregroundColor}/>
      <LoadingLabel color={foregroundColor}>{label}</LoadingLabel>  
    </Container>
  )
}

export const Waiting = () => {
  const { ready } = useContext(AppContext);
  return (
    <Loader isFull isTransitionMode={false} isLoading={!ready} top={'auto'} zIndex={1} color={themeColor.black} bgColor={themeColor.white} />
  )
}

const LoadingBarProgress = styled(a.div)`
  height: 20px;
  background: ${props => props.color};  
`

const LoadingBarFrame = styled.div`
  position: relative;
  width: 60%;
  height: 14px;
  border:  solid 1px ${props => props.color};
  --notchSize: 6px;
  
  clip-path: 
    polygon(
      0% 0%, 
      100% 0%, 
      100% 100%, 
      var(--notchSize) 100%, 
      0% calc(100% - var(--notchSize))
    );

  &:after {
    content: '';
    height: 0;
    border:  solid 1px ${props => props.color};
    background-color: ${props => props.color};
    position: absolute;
    transform: rotate(45deg);
    width: 10px;
    left: -3px;
    bottom: 1px;    
  }
`

const LoadingBarContainer = styled(a.div)`
  position: absolute;
  top: -30px;
  left: 0px;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
`

const LoadingBar = ({color}) => {
  const [finished, set] = useState(false)
  const [width, setWidth] = useState(`0%`)

  useEffect(() => {
    THREE.DefaultLoadingManager.onLoad = () => set(true)
    THREE.DefaultLoadingManager.onProgress = (url, itemsLoaded, itemsTotal) =>
      setWidth(`${(itemsLoaded / itemsTotal) * 100}%`)
    return () => {
      THREE.DefaultLoadingManager.onProgress = () => {}
    };
  }, [])

  const props = useTransition(finished, null, {
    from: { opacity: 1, width: `0%` },
    leave: { opacity: 1 },
    update: { width },
  })

  return props.map(
    ({ item: finished, key, props: { opacity, width } }) =>
      (
        <LoadingBarContainer key={key} style={{ opacity }}>
          <LoadingBarFrame color={color}>
            <LoadingBarProgress color={color} style={{ width }} />
          </LoadingBarFrame>              
        </LoadingBarContainer>
      )
  )
}
export const useLoading = () => {
  const [loading, setLoading] = useState(false);
  const currentTimerRef = useRef();
  useEffect(() => {    
    return () => {
      clearTimeout(currentTimerRef.current);
    }
  }, [])

  useEffect(() => {    
    console.log ({loading});
  }, [loading])

  const onStartLoading = () => {
    clearTimeout(currentTimerRef.current);
    setLoading(true);
  }

  const onEndLoading = () => {
    clearTimeout(currentTimerRef.current);
    currentTimerRef.current = setTimeout(() => setLoading(false), 500);
  }
  return [loading, onStartLoading, onEndLoading]
} 

export const Start = ({ children, loading }) => {
  const { started, setStarted, setAdvancedMode, pages, current, pageStep } = useContext(AppContext);

  usePreload(pages, current, pageStep, loading, started);

  const onStartClick = () => {
    setAdvancedMode(true);
    setStarted(true);
  };

  return !started ? (
    <CoverPage disabled={!pages} onStart={onStartClick} />
  ) : children;
}